import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { StatusBadge } from "@/pages/AccountOrders";
import { QtyStepper } from "@/components/ui-bits";
import { useLang } from "@/lib/i18n";

export const AccountOrderDetail = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { t, lang, pick } = useLang();
  const locale = lang === "fr" ? "fr-FR" : "nl-NL";
  const [order, setOrder] = useState(null);
  const [reason, setReason] = useState("");
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");

  const loadOrder = () => {
    api.get(`/orders/${orderId}`)
      .then((d) => setOrder(d.data))
      .catch(() => navigate("/account/bestellingen"));
  };
  useEffect(() => { loadOrder(); }, [orderId]);

  if (!order) {
    return <div className="min-h-screen bg-obsidian pt-32 px-6"><div className="max-w-3xl h-40 bg-ink animate-pulse" /></div>;
  }

  const canCancel = ["pending", "paid"].includes(order.status);
  const canReturn = ["paid", "shipped", "delivered"].includes(order.status);
  const activeReturn = order.return_requests?.find((r) => ["requested", "approved"].includes(r.status));

  const cancel = async () => {
    setError("");
    try {
      await api.post(`/orders/${orderId}/cancel`);
      loadOrder();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const requestReturn = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.post(`/orders/${orderId}/return-request`, { reason });
      setMsg(t("account.returnOk"));
      loadOrder();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  return (
    <div className="px-6 sm:px-12 lg:px-20 pt-28 pb-24">
      <button data-testid="back-to-orders" onClick={() => navigate("/account/bestellingen")} className="flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] uppercase text-smoke hover:text-gold transition-colors mb-10">
        <ChevronLeft size={14} /> {t("account.allOrders")}
      </button>

      <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
        <div>
          <h1 data-testid="order-detail-number" className="font-serif font-light text-cream text-3xl">{order.order_id}</h1>
          <p className="font-mono text-[10px] text-smoke mt-2">
            {new Date(order.created_at).toLocaleDateString(locale, { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}
          </p>
        </div>
        <StatusBadge status={order.status} />
      </div>

      {order.tracking_number && (
        <div data-testid="tracking-box" className="border border-gold/25 bg-ink p-5 mb-10 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-gold/85">{t("account.tracking")}</p>
            <p className="font-mono text-sm text-cream mt-1.5">{order.tracking_number}</p>
          </div>
          <span className="font-mono text-[10px] text-smoke uppercase tracking-[0.2em]">Verzonden via PostNL</span>
        </div>
      )}

      <div className="grid lg:grid-cols-[2fr_1fr] gap-12">
        <div className="space-y-4">
          {order.items.map((i) => (
            <div key={i.slug} data-testid={`order-item-${i.slug}`} className="flex gap-5 border border-gold/10 bg-ink p-4">
              <div className={`w-20 h-24 shrink-0 ${i.image_style === "studio" || i.image_url.startsWith("/products") ? "bg-[#ECEAE3]" : "bg-elevate"}`}>
                <img src={i.image_url} alt="" className={`w-full h-full ${i.image_url.startsWith("/products") ? "object-contain p-2" : "object-cover"}`} />
              </div>
              <div className="flex-1">
                <p className="font-mono text-[9px] tracking-[0.3em] uppercase text-gold/80">{i.brand}</p>
                <p className="font-serif text-lg text-cream mt-1">{pick(i, "model")}</p>
                <p className="font-mono text-[10px] text-smoke mt-1">{i.quantity} × € {i.unit_price.toFixed(2).replace(".", ",")}</p>
              </div>
            </div>
          ))}
          {error && <p data-testid="order-error" className="text-xs text-red-400">{error}</p>}

          {canCancel && (
            <button data-testid="cancel-order-button" onClick={cancel} className="w-full border border-red-400/40 text-red-300 font-mono text-[10px] tracking-[0.3em] uppercase py-4 hover:bg-red-500/10 transition-colors">
              {order.status === "paid" ? t("account.cancelPaid") : t("account.cancel")}
            </button>
          )}

          {canReturn && !activeReturn && (
            <form data-testid="return-form" onSubmit={requestReturn} className="border border-gold/15 bg-ink p-6 mt-8">
              <h3 className="font-mono text-[10px] tracking-[0.3em] uppercase text-gold/85 mb-4">{t("account.returnQ")}</h3>
              <textarea
                data-testid="return-reason-input"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                required
                minLength={10}
                placeholder={t("account.returnPh")}
                className="w-full bg-obsidian border border-gold/15 px-4 py-3 text-sm text-cream placeholder:text-smoke focus:outline-none focus:border-gold/50"
              />
              <button type="submit" data-testid="return-submit-button" className="mt-4 border border-gold/40 text-gold font-mono text-[10px] tracking-[0.3em] uppercase px-8 py-3.5 hover:bg-gold/10 transition-colors">
                {t("account.returnBtn")}
              </button>
            </form>
          )}

          {activeReturn && (
            <div data-testid="return-status-box" className="border border-amber-300/30 bg-amber-300/5 p-5 mt-8">
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-amber-300">{activeReturn.status === "approved" ? t("account.returnBoxOk") : t("account.returnBox")}</p>
              <p className="text-sm text-mist mt-2">{t("account.returnInfo")}</p>
            </div>
          )}

          {msg && <p data-testid="return-success" className="text-xs text-emerald-300">{msg}</p>}
        </div>

        <div className="h-fit border border-gold/20 bg-elevate p-8">
          <h2 className="font-mono text-[10px] tracking-[0.3em] uppercase text-gold/85 mb-6">{t("account.overview")}</h2>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between text-mist"><span>{t("cart.subtotal")}</span><span>€ {order.subtotal?.toFixed(2).replace(".", ",")}</span></div>
            {order.discount > 0 && <div className="flex justify-between text-gold"><span>{t("cart.discount")} {order.coupon_code && `(${order.coupon_code})`}</span><span>−€ {order.discount.toFixed(2).replace(".", ",")}</span></div>}
            <div className="flex justify-between text-mist"><span>{t("account.shipMethod")} ({order.shipping_method})</span><span>{order.shipping_cost === 0 ? t("checkout.free") : `€ ${order.shipping_cost.toFixed(2).replace(".", ",")}`}</span></div>
            <div className="flex justify-between items-baseline border-t border-gold/10 pt-4">
              <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-smoke">{t("cart.total")}</span>
              <span data-testid="order-total" className="font-serif text-2xl text-gold">€ {order.total?.toFixed(2).replace(".", ",")}</span>
            </div>
          </div>
          <div className="border-t border-gold/10 mt-6 pt-6">
            <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-smoke mb-3">{t("account.shipTo")}</p>
            <p className="text-sm text-cream">{order.address?.name}</p>
            <p className="text-sm text-mist">{order.address?.street} {order.address?.house_number}, {order.address?.postal_code} {order.address?.city}, {order.address?.country}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountOrderDetail;
